<template>
    <div class="grid">
        <div class="unit whole">
            <h1>Marking tasks</h1>
            <p class="lead">View and mark tasks assigned to pupils.</p>

            <v-alert v-if="tasksForPupilsNotInClasses.length > 0" type="info">
                <div>
                    You have {{ tasksForPupilsNotInClasses.length }} task{{
                        tasksForPupilsNotInClasses.length == 1 ? '' : 's'
                    }}
                    listed for pupils who are no longer in your class. Would you like to move
                    {{ tasksForPupilsNotInClasses.length == 1 ? 'this task' : 'all of these' }} to the archive?
                </div>
                <div class="d-flex justify-end mt-3">
                    <v-btn variant="outlined" @click="archiveTasksForPupilsNotInClass"
                        >Archive Task{{ tasksForPupilsNotInClasses.length == 1 ? '' : 's' }}
                    </v-btn>
                </div>
            </v-alert>

            <v-alert
                v-if="teacher && teacher.use_automated_marking === undefined"
                icon="mdi-robot"
                type="info"
                color="tertiary-container"
                closable
                title="Introducing AI-Assisted Marking for HOTS"
                :class="{ 'mt-3': tasksForPupilsNotInClasses.length > 0 }"
            >
                <p class="mt-5">
                    Designed to support your judgement - not replace it - this feature helps you gain quick insights
                    into learner responses.
                </p>
                <p class="mt-2">
                    More information can be found
                    <a id="stonly-open-ai-assisted-marking" href="#">here</a>. You can manage this feature any time in
                    your profile.
                </p>

                <div class="d-flex justify-end mt-3">
                    <v-btn variant="outlined" @click="store.updateTeacher({ use_automated_marking: false })"
                        >Dismiss
                    </v-btn>
                    <v-btn color="accent" class="ml-3" @click="store.updateTeacher({ use_automated_marking: true })"
                        >Turn on AI-assisted marking
                    </v-btn>
                </div>
            </v-alert>
        </div>
    </div>
    <div class="grid">
        <div class="unit whole">
            <div class="pb-5 d-flex justify-space-between align-center">
                <v-row no-gutters>
                    <v-col>
                        <v-select
                            v-model="classFilter"
                            class="mr-5"
                            density="comfortable"
                            hide-details
                            label="Class"
                            :items="classesItems"
                            item-title="name"
                            item-value="uid"
                            rounded="xl"
                            multiple
                            variant="outlined"
                        ></v-select>
                    </v-col>
                    <v-col>
                        <v-select
                            v-model="groupFilter"
                            class="mr-5"
                            density="comfortable"
                            hide-details
                            label="Group"
                            :items="groupsItems"
                            item-title="name"
                            item-value="uid"
                            multiple
                            rounded="xl"
                            variant="outlined"
                        ></v-select>
                    </v-col>
                    <v-col>
                        <v-select
                            v-model="statusFilter"
                            class="mr-5"
                            density="comfortable"
                            hide-details
                            label="Status"
                            :items="statusesItems"
                            item-title="name"
                            item-value="id"
                            multiple
                            rounded="xl"
                            variant="outlined"
                        ></v-select>
                    </v-col>
                </v-row>
                <v-btn
                    height="48"
                    :color="
                        statusFilter.length === 0 && classFilter.length === 0 && groupFilter.length === 0
                            ? ''
                            : 'secondary-container'
                    "
                    :disabled="statusFilter.length === 0 && classFilter.length === 0 && groupFilter.length === 0"
                    append-icon="mdi-close"
                    @click="
                        statusFilter = [];
                        classFilter = [];
                        groupFilter = [];
                    "
                    >Clear filters
                </v-btn>
            </div>
            <div>
                <v-expansion-panels multiple v-model="openPanels">
                    <v-expansion-panel
                        v-for="group of filteredGroupedAndPaginatedItems"
                        :key="`task-group-${group.task.id}`"
                        elevation="0"
                    >
                        <v-expansion-panel-title>
                            <div class="d-flex flex-row justify-space-between w-100 pr-5">
                                <div class="d-flex flex-row align-center">
                                    <TaskIcon :task="group.task" />
                                    <div class="ml-5">{{ group.task.book.title }} - {{ group.task.title }}</div>
                                </div>
                                <div
                                    v-if="
                                        (statusFilter.length > 1 ||
                                            statusFilter.some((s) => ['Completed', 'Archived'].includes(s))) &&
                                        !statusFilter.some((s) => ['Submitted', 'Assigned'].includes(s))
                                    "
                                >
                                    {{ group.pupilTasks.length }} {{ group.pupilTasks.length === 1 ? 'task' : 'tasks' }}
                                </div>
                                <div v-else>{{ group.submitted }} of {{ group.pupilTasks.length }} submitted</div>
                            </div>
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                            <TeacherMarkPupilTaskList
                                :task="group.task"
                                :pupilTasks="group.pupilTasks"
                            ></TeacherMarkPupilTaskList>
                        </v-expansion-panel-text>
                    </v-expansion-panel>
                </v-expansion-panels>
                <v-btn
                    v-if="filteredAndGroupedItems.length > itemsToShow"
                    class="mt-5"
                    color="secondary-container"
                    @click="itemsToShow += 20"
                    >Load more...</v-btn
                >
            </div>
        </div>
    </div>
    <v-overlay :model-value="!isLoaded.pupilTasks" class="align-center justify-center">
        <v-progress-circular color="primary" indeterminate size="64"></v-progress-circular>
    </v-overlay>
</template>
<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import { PupilTask, Task } from '@ilteducation/giglets-types';
import { PupilTaskWithNoAccess } from '@/types/frontend';
import { useFetchUsers, User } from '@ilteducation/customer-mgmt';

const route = useRoute();
const router = useRouter();
definePage({ meta: { area: 'marking' } });
const confirm = useConfirmDialog();
const store = useTeacherStore();
store.fetchGroups();
store.fetchClasses();
store.fetchPupilTasks();
store.fetchTeacher();
const { classes, groups, pupilTasks, isLoaded, teacher } = storeToRefs(store);
const { fetchUsersByIds } = useFetchUsers();

const selectedTasks = ref<number[]>([]);
const taskTypeFilter = ref<string>();
const pupilFilter = ref<number>();
const bookFilter = ref<number>();
const statuses = ['Assigned', 'Submitted', 'Completed', 'Archived'];
const statusFilter = ref<string[]>([]);
const classFilter = ref<string[]>([]);
const groupFilter = ref<string[]>([]);
const openPanels = ref<number[]>([]);

watch(pupilTasks, () => {
    if (pupilTasks.value.some((pt) => isOverdue(pt))) {
        statuses.splice(1, 0, 'Overdue');
    }
});

watch([statusFilter, classFilter, groupFilter], () => {
    openPanels.value = [];
});

const rq = route.query;
if (rq.status === 'submitted') {
    statusFilter.value = ['Submitted'];
}
if (rq.status === 'assigned') {
    statusFilter.value = ['Assigned'];
}
if (rq.status === 'completed') {
    statusFilter.value = ['Completed'];
}
if (rq.book) {
    bookFilter.value = parseInt(rq.book as string);
}

// update query params when filters change
watch([statusFilter, classFilter, groupFilter], () => {
    const params: Record<string, string | string[]> = {};
    if (statusFilter.value.length > 0) {
        params.status = statusFilter.value.map((s) => s.toLowerCase());
    }
    if (classFilter.value.length > 0) {
        params.class = classFilter.value.map((c) => c.toString());
    }
    if (groupFilter.value.length > 0) {
        params.group = groupFilter.value.map((g) => g.toString());
    }
    if (Object.keys(params).length > 0) {
        // @ts-ignore
        router.replace({ name: route.name!, query: params });
    } else {
        // @ts-ignore
        router.replace({ name: route.name! });
    }
});

const embellishedItems = computed(() => {
    return pupilTasks.value.map((pt: PupilTask & { noAccess?: boolean }) => {
        // make a copy, otherwise when we modify pt, it causes reactivity which is very slow
        const copy = { ...pt };
        if (
            !(
                classes.value.some((c) => c.pupils.some((p) => p.id === pt.pupil.id)) ||
                teacher.value?.virtual_pupil.id === pt.pupil.id
            )
        ) {
            copy.noAccess = true;
        } else {
            copy.noAccess = false;
        }
        return copy;
    });
});

let noAccessPupils: User[] = [];

watch(embellishedItems, async () => {
    const noAccessPupilUids = Array.from(
        new Set(embellishedItems.value.filter((pt) => pt.noAccess).map((pt) => pt.pupil.uid)),
    );
    if (noAccessPupilUids.length === 0) {
        noAccessPupils = [];
        return;
    }
    noAccessPupils = await fetchUsersByIds(noAccessPupilUids);
});

function noAccessPupilName(uid: string) {
    const user = noAccessPupils.find((u) => u.uid === uid);
    if (user) {
        return user.name;
    }
    return 'Unknown pupil';
}

provide('noAccessPupilName', noAccessPupilName);

function isBeforeQ(pt: PupilTask) {
    return pt.task.types.map((tt) => tt.code).includes('before');
}

const filteredItems = computed(() => {
    return embellishedItems.value
        .filter((pt) => {
            if (taskTypeFilter.value === 'Normal task') {
                return !pt.task.hots && !pt.task.aspire && !pt.task.reading_test && !isBeforeQ(pt);
            } else if (taskTypeFilter.value === 'HOTS') {
                return pt.task.hots;
            } else if (taskTypeFilter.value === 'Reading test') {
                return pt.task.reading_test;
            } else if (taskTypeFilter.value === 'Before questions') {
                return isBeforeQ(pt);
            } else if (taskTypeFilter.value === 'ASPIRE questions') {
                return pt.task.aspire;
            }
            return true;
        })
        .filter((pt) => {
            if (pupilFilter.value) {
                return pupilFilter.value === pt.pupil.id;
            }
            return true;
        })
        .filter((pt) => {
            if (bookFilter.value) {
                return bookFilter.value === pt.task.book.id;
            }
            return true;
        })
        .filter((pt) => {
            if (statusFilter.value.length > 0) {
                return (
                    (statusFilter.value.includes('Assigned') && pt.status === 1) ||
                    (statusFilter.value.includes('Overdue') && isOverdue(pt)) ||
                    (statusFilter.value.includes('Submitted') && pt.status === 2) ||
                    // Include "Completed" and "Awaiting pupil acknowledgement"
                    (statusFilter.value.includes('Completed') && (pt.status === 3 || pt.status === 5)) ||
                    (statusFilter.value.includes('Archived') && pt.status === 6)
                );
            }
            return true;
        })
        .filter((pt) => {
            if (classFilter.value.length > 0) {
                return classes.value.some(
                    (c) => classFilter.value.includes(c.uid) && c.pupils.some((p) => p.id === pt.pupil.id),
                );
            }
            return true;
        })
        .filter((pt) => {
            if (groupFilter.value.length > 0) {
                return groups.value.some(
                    (g) => groupFilter.value.includes(g.uid) && g.pupils.some((p) => p.id === pt.pupil.id),
                );
            }
            return true;
        });
});

const filteredAndGroupedItems = computed(() => {
    const res: {
        [taskID: number]: {
            task: Task;
            submitted: number;
            newestAssignmentDate: string;
            pupilTasks: PupilTaskWithNoAccess[];
        };
    } = {};
    filteredItems.value.forEach((item) => {
        if (!res[item.task.id]) {
            res[item.task.id] = {
                task: item.task,
                submitted: 0,
                pupilTasks: [],
                newestAssignmentDate: item.assigned_date || new Date(0).toISOString(),
            };
        }
        res[item.task.id].pupilTasks.push(item);
        if (item.status === 2) {
            res[item.task.id].submitted++;
        }
        if (item.assigned_date && new Date(item.assigned_date) > new Date(res[item.task.id].newestAssignmentDate)) {
            res[item.task.id].newestAssignmentDate = item.assigned_date;
        }
    });
    return Object.values(res).sort((a, b) => {
        return b.newestAssignmentDate.localeCompare(a.newestAssignmentDate);
    });
});

const itemsToShow = ref(20);

const filteredGroupedAndPaginatedItems = computed(() => {
    const items = filteredAndGroupedItems.value;
    return items.slice(0, itemsToShow.value);
});

const existingClasses = computed(() => {
    return pupilTasks.value
        .map((pt) => classes.value.filter((c) => c.pupils.some((p) => p.id === pt.pupil.id)))
        .flat()
        .filter((c, index, self) => self.findIndex((cl) => cl.uid === c.uid) === index);
});

const classesItems = computed(() => {
    return classes.value.map((cl) => ({
        ...cl,
        props: {
            disabled: !existingClasses.value.some((vc) => vc.uid === cl.uid),
        },
    }));
});

const existingGroups = computed(() => {
    return pupilTasks.value
        .map((pt) => groups.value.filter((g) => g.pupils.some((p) => p.id === pt.pupil.id)))
        .flat()
        .filter((g, index, self) => self.findIndex((gl) => gl.uid === g.uid) === index);
});

const groupsItems = computed(() => {
    return groups.value.map((g) => ({
        ...g,
        props: {
            disabled: !existingGroups.value.some((vg) => vg.uid === g.uid),
        },
    }));
});

const statusesItems = computed(() => {
    return statuses.map((status) => ({
        id: status,
        name: status,
        props: {
            disabled: !pupilTasks.value.some((pt) => {
                if (status === 'Assigned') return pt.status === 1;
                if (status === 'Overdue') return isOverdue(pt);
                if (status === 'Submitted') return pt.status === 2;
                if (status === 'Completed') return pt.status === 3 || pt.status === 5;
                if (status === 'Archived') return pt.status === 6;
                return false;
            }),
        },
    }));
});

function isOverdue(pupilTask: PupilTask) {
    return pupilTask.status === 1 && pupilTask.due_date !== undefined && new Date(pupilTask.due_date) < new Date();
}

const tasksForPupilsNotInClasses = computed(() => {
    return embellishedItems.value.filter((pt) => pt.noAccess && !pt.shared && pt.status !== 6);
});

async function archiveTasksForPupilsNotInClass() {
    const res = await confirm(
        'Archive tasks?',
        `This will move ${tasksForPupilsNotInClasses.value.length} task${tasksForPupilsNotInClasses.value.length === 1 ? '' : 's'} to your archive. Ok?`,
    );
    if (res) {
        try {
            for (const pupilTask of tasksForPupilsNotInClasses.value) {
                await store.updatePupilTask(pupilTask.id, { status: 6 });
            }
        } finally {
            selectedTasks.value = [];
            store.fetchPupilTasks().then(() => {});
        }
    }
}
</script>
<style lang="scss" scoped></style>
